import { ConversationStatus, ConversationType, MessageType, TInboxConversation, User } from '@talon/web/lib/api';
import { Box, Button, Select, Text } from 'grommet';
import { EmptyState } from '../EmptyState';
import { StatusPill } from '../StatusPill';
import { isTalentManagerUser } from '../../../utils/userRoles';

export interface IConversationListProps {
  selectConversation: (id: string) => void;
  selectedId?: string;
  setStatus: (status: ConversationStatus) => void;
  status: ConversationStatus;
  conversations: TInboxConversation[];
  openCount: number;
  usersById?: Record<string, User>;
  typeFilter?: string;
  setTypeFilter?: (type: string) => void;
}

export const ConversationList = ({
  selectConversation,
  selectedId,
  conversations,
  setStatus,
  status,
  openCount,
  usersById = {},
  typeFilter,
  setTypeFilter,
}: IConversationListProps) => (
  <Box width="340px" fill="vertical" border={{ side: 'right', color: '#E3E3E3' }} flex={false}>
    <Box
      direction="row"
      align="center"
      justify="between"
      pad={{ vertical: '12px', horizontal: '14px' }}
      border={{ side: 'bottom', color: '#E3E3E3' }}
      gap="xsmall"
    >
      <Box direction="row" flex={false} pad="3px" background="#F2F2F2" round="8px">
        <Button
          plain
          aria-pressed={status === ConversationStatus.OPEN}
          onClick={() => setStatus(ConversationStatus.OPEN)}
        >
          <Box
            pad={{ vertical: '4px', horizontal: '10px' }}
            round="6px"
            background={status === ConversationStatus.OPEN ? 'white' : undefined}
          >
            <Text size="13px" weight={600} color={status === ConversationStatus.OPEN ? '#222' : '#666'}>
              Open {openCount}
            </Text>
          </Box>
        </Button>
        <Button
          plain
          aria-pressed={status === ConversationStatus.CLOSED}
          onClick={() => setStatus(ConversationStatus.CLOSED)}
        >
          <Box
            pad={{ vertical: '4px', horizontal: '10px' }}
            round="6px"
            background={status === ConversationStatus.CLOSED ? 'white' : undefined}
          >
            <Text size="13px" weight={600} color={status === ConversationStatus.CLOSED ? '#222' : '#666'}>
              Closed
            </Text>
          </Box>
        </Button>
      </Box>
      {typeFilter !== undefined && setTypeFilter && (
        <Box width="124px" flex={false}>
          <Select
            options={['All types', 'Support', 'Talent manager']}
            value={typeFilter}
            onChange={({ option }) => setTypeFilter(option)}
            size="small"
            style={{ width: '100%', minWidth: 0 }}
          />
        </Box>
      )}
    </Box>
    <Box overflow={{ vertical: 'auto' }} flex>
      {conversations.map((conversation) => {
        const last = conversation.chatMessages?.reduce(
          (latest, message) =>
            !latest || (message.created_at && message.created_at > (latest.created_at || '')) ? message : latest,
          undefined as (typeof conversation.chatMessages)[number] | undefined
        );
        const updated = new Date(conversation.updated_at);
        const today = updated.toDateString() === new Date().toDateString();
        const owner =
          conversation.owner || (conversation.owner_user_id ? usersById[conversation.owner_user_id] : undefined);
        const account = conversation.group?.name || owner?.active_group?.name || owner?.groups?.[0]?.name;
        const role = owner
          ? owner.is_admin
            ? 'Admin'
            : owner.performer_id
              ? 'Performer'
              : isTalentManagerUser(owner)
                ? 'Talent manager'
                : 'Agency'
          : conversation.conversation_type === ConversationType.TALENT_MANAGER
            ? 'Talent manager'
            : '';
        return (
          <Button
            key={conversation.id}
            plain
            onClick={() => selectConversation(conversation.id)}
            style={{ width: '100%', textAlign: 'left' }}
          >
            <Box
              pad={{ vertical: '10px', horizontal: '14px' }}
              border={{ side: 'bottom', color: '#EDEDED' }}
              background={selectedId === conversation.id ? 'brand-tint' : 'white'}
              style={{ minHeight: 64 }}
            >
              <Box direction="row" align="center" gap="xsmall">
                {!!conversation.unreadCount && <Box width="8px" height="8px" round="full" background="brand" />}
                <Box flex>
                  <Text size="14px" weight={conversation.unreadCount ? 600 : 400} color="#222">
                    {owner
                      ? `${owner.first_name || ''} ${owner.last_name || ''}`.trim() || conversation.name
                      : conversation.name}
                  </Text>
                </Box>
                {conversation.isNew && <StatusPill label="New" tone="warning" />}
                <Text size="12px" color="#777">
                  {today
                    ? updated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
                    : updated.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </Text>
              </Box>
              {(account || role) && (
                <Text size="12px" color="#666">
                  {[account, role].filter(Boolean).join(' · ')}
                </Text>
              )}
              {last?.text && (
                <Box direction="row" justify="between" gap="small">
                  <Text
                    size="13px"
                    color="#555"
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {last.type === MessageType.SUPPORT_MESSAGE ? 'You: ' : ''}
                    {last.text}
                  </Text>
                  {!!conversation.unreadCount && (
                    <Box background="brand" round="full" pad={{ horizontal: '6px' }}>
                      <Text size="12px" color="white">
                        {conversation.unreadCount}
                      </Text>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Button>
        );
      })}
      {!conversations.length && (
        <EmptyState
          title={status === ConversationStatus.OPEN ? 'Inbox zero' : 'No closed conversations yet.'}
          body={status === ConversationStatus.OPEN ? 'New support requests show up here.' : ''}
        />
      )}
    </Box>
  </Box>
);
